import React, { useMemo, FC } from "react";
import cn from "classnames";
import { useField } from "formik";
import TextareaAutosize from "react-textarea-autosize";
import PhoneInput from "react-phone-number-input/input";
import { IFormField } from "src/@types/form";
import { FormField } from ".";
import { TEXT_INPUT_STYLE_VARIANTS } from "./constants";
import { FormFieldVariants, InputChangeUnion } from "./types";

export const TextInput: FC<IFormField> = ({
  className,
  fieldClassName,
  labelClassName,
  id,
  label,
  variant = FormFieldVariants.PRIMARY,
  placeholder,
  isTextArea,
  ...props
}) => {
  const fieldId = id || props.name;

  const [fieldProps, fieldMeta, fieldHelpers] = useField<string>(fieldId);
  const { value, onChange, ...field } = fieldProps;
  const { error, touched } = fieldMeta;
  const { setValue } = fieldHelpers;

  const isShownError = Boolean((touched || value) && error);

  const combinedClassNames = cn(TEXT_INPUT_STYLE_VARIANTS[variant], className, {
    "outline outline-2.5 outline-offset-2 outline-red-base transition-none":
      isShownError,
  });

  const isTelField = props?.type === "tel";

  const InputComponent = useMemo(() => {
    if (isTelField) return PhoneInput;
    if (isTextArea) return TextareaAutosize;
    return "input";
  }, [isTextArea, isTelField]);

  const handleChange = (e: InputChangeUnion) => {
    if (isTelField) setValue((e || "") as string);
    else onChange(e);
  };

  return (
    <FormField
      className={fieldClassName}
      labelClassName={labelClassName}
      variant={variant}
      label={label}
      labelFor={fieldId}
    >
      <InputComponent
        id={fieldId}
        className={combinedClassNames}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        {...props}
        {...field}
        // Placed here to prevent the number input from changing when scrolling
        onWheel={(e) => props.type === "number" && e.target.blur()}
      />
    </FormField>
  );
};
